interface PeerAudio {
  element: HTMLAudioElement;
  source: MediaStreamAudioSourceNode;
  gain: GainNode;
  panner: StereoPannerNode | null;
}

/**
 * Spatial Audio: pro Peer laeuft der Mikrofon-Stream durch einen GainNode,
 * dessen Wert mit der Avatar-Distanz faellt (gainForDistance), und danach
 * durch einen Stereo-Panner, der die Richtung abbildet. Der Panner sitzt
 * bewusst hinter dem Gain — die Lautstaerkekurve bleibt davon unberuehrt.
 *
 * Chrome-Eigenheit: ein Remote-MediaStream liefert nur dann Audio an
 * WebAudio, wenn er zusaetzlich an ein (stummes) Media-Element gebunden ist —
 * daher das versteckte <audio muted>-Element pro Peer.
 */
class SpatialAudio {
  private ctx: AudioContext | null = null;
  private peers = new Map<string, PeerAudio>();

  /** Muss aus einer User-Geste heraus passieren (Autoplay-Policy). */
  resume(): void {
    this.ctx ??= new AudioContext();
    if (this.ctx.state === 'suspended') void this.ctx.resume();
  }

  attach(peerId: string, stream: MediaStream): void {
    this.resume();
    if (!this.ctx) return;
    this.detach(peerId);

    const element = document.createElement('audio');
    element.srcObject = stream;
    element.muted = true;
    element.autoplay = true;
    document.body.appendChild(element);

    const source = this.ctx.createMediaStreamSource(stream);
    const gain = this.ctx.createGain();
    gain.gain.value = 1;
    // Aeltere Browser ohne StereoPannerNode bekommen weiterhin reines Mono
    const panner = typeof this.ctx.createStereoPanner === 'function' ? this.ctx.createStereoPanner() : null;
    if (panner) source.connect(gain).connect(panner).connect(this.ctx.destination);
    else source.connect(gain).connect(this.ctx.destination);

    this.peers.set(peerId, { element, source, gain, panner });
  }

  setGain(peerId: string, value: number): void {
    const peer = this.peers.get(peerId);
    if (!peer || !this.ctx) return;
    // kleines Ramping gegen Knackser
    peer.gain.gain.setTargetAtTime(value, this.ctx.currentTime, 0.08);
  }

  /** Stereo-Position: -1 links, 0 mittig, +1 rechts. */
  setPan(peerId: string, value: number): void {
    const peer = this.peers.get(peerId);
    if (!peer?.panner || !this.ctx) return;
    peer.panner.pan.setTargetAtTime(value, this.ctx.currentTime, 0.12);
  }

  detach(peerId: string): void {
    const peer = this.peers.get(peerId);
    if (!peer) return;
    peer.source.disconnect();
    peer.gain.disconnect();
    peer.panner?.disconnect();
    peer.element.srcObject = null;
    peer.element.remove();
    this.peers.delete(peerId);
  }

  detachAll(): void {
    for (const peerId of [...this.peers.keys()]) this.detach(peerId);
  }
}

export const spatialAudio = new SpatialAudio();
